import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { css, StyleSheet } from "aphrodite";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CustomDatagrid,
  CustomRowBase,
} from "../../components/datagrid/CustomDatagrid";
import axios from "axios";
import { headers } from "../../components/core/store/localStorage";
import Colors from "../../components/constants/Colors";
import { FilterFinanceiro } from "../../components/filter/FilterFinanceiro";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PictureAsPdf, TableView } from "@mui/icons-material";

type GridRow = {
  id: string;
  nome: string;
  totalGeral: number;
  [key: string]: any; // pros meses dinâmicos
};

const currentUser = sessionStorage.getItem("userName");
const currentEmp = sessionStorage.getItem("empName");
const API_URL = sessionStorage.getItem("apiUrl");
const PAGE_SIZE = 30;

export const FinanceiroAlunos = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = window.location.href;
  let link = "";
  link = id.includes("?page=") ? id.charAt(38) : "";

  const [trigger, setTrigger] = useState(false);
  const [loading, setLoading] = useState(false);
  const [titleLoading, setTitleLoading] = useState("");
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [rows, setRows] = useState<GridRow[]>([]);
  const [searchNome, setSearchNome] = useState("");
  const [head, setHead] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [extraColumns, setExtraColumns] = useState<any[]>([]);
  const [visibleCols, setVisibleCols] = useState<string[]>([
    "nome",
    "totalGeral",
  ]);
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [searchSituacao, setSearchSituacao] = useState<string[]>([]);

  useEffect(() => {
    if (API_URL === null) window.location.href = "/";
  }, []);

  useEffect(() => {
    searchStudents();
  }, [page]);

  const fetchAllPages = async () => {
    setLoading(true);
    setTrigger(false);
    setTitleLoading("Preparando todas as páginas para exportação...");

    try {
      let totalPags = rowCount / PAGE_SIZE;
      const allDataRows = [];

      for (let i = 1; i <= totalPags; i++) {
        let url = `${API_URL}/aluno/financeiro?page=${i}`;
        if (searchNome) url += `&nome=${searchNome}`;
        if (dataInicial) url += `&dataInicial=${dataInicial}-01`;
        if (dataFinal) url += `&dataFinal=${dataFinal}-31`;
        if (Array.isArray(searchSituacao) && searchSituacao.length > 0) {
          const situacaoParam = searchSituacao
            .map((s) => `'${String(s).trim()}'`)
            .join(",");
          url += `&situacao=${encodeURIComponent(situacaoParam)}`;
        }

        const res = await axios.get(url, headers());
        const alunos = res.data.alunos || [];

        const processed = alunos.map((a: any) => {
          const perMonth: Record<string, number> = {};
          let totalGeral = 0;
          for (const c of a.cobrancas || []) {
            const mk = monthKey(c.dataPagamento);
            if (!mk) continue;
            const vp = moneyToNumber(c.valorPago);
            perMonth[mk] = (perMonth[mk] || 0) + vp;
            totalGeral += vp;
          }

          const monthFields: Record<string, any> = {};
          extraColumns.forEach((col) => {
            if (col.field !== "totalGeral") {
              monthFields[col.field] = perMonth[col.field] ?? null;
            }
          });

          return { nome: a.nome, ...monthFields, totalGeral };
        });

        allDataRows.push(...processed);
      }

      return allDataRows;
    } catch (e) {
      console.error("Erro ao buscar todas as páginas", e);
      return [];
    } finally {
      setLoading(false);
      setTrigger(true);
    }
  };

  const moneyToNumber = (v?: string) => {
    if (!v) return 0;
    const s = v.trim();
    if (!s) return 0;
    return Number(s.replace(/\./g, "").replace(",", "."));
  };

  const monthKey = (date?: string) => (date ? date.slice(0, 7) : null); // YYYY-MM
  const monthLabel = (m: string) => `${m.slice(5, 7)}/${m.slice(0, 4)}`; // MM/YYYY

  const formatBRL = (value: any) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const searchStudents = async () => {
    setNotFound(false);
    setTrigger(false);
    setLoading(true);
    setTitleLoading("Pesquisando alunos...");

    const safeValueFormatter = (arg: any) => {
      const value = arg?.value ?? arg;
      if (value === null || value === undefined || value === "") return "";
      return formatBRL(value);
    };

    try {
      const apiPage = page + 1;
      let url = `${API_URL}/aluno/financeiro?page=${apiPage}`;

      if (searchNome) {
        url += `&nome=${searchNome}`;
      }

      if (dataInicial) {
        url += `&dataInicial=${dataInicial}-01`;
      }
      if (dataFinal) {
        url += `&dataFinal=${dataFinal}-31`;
      }

      if (Array.isArray(searchSituacao) && searchSituacao.length > 0) {
        const situacaoParam = searchSituacao
          .map((s) => `'${String(s).trim()}'`)
          .join(",");

        url += `&situacao=${encodeURIComponent(situacaoParam)}`;
      }

      const response = await axios.get(url, headers());
      console.log(response.data);

      const { alunos, totalPages } = response.data;

      if (!alunos || alunos.length === 0) {
        setLoading(false);
        setNotFound(true);
        setRows([]);
        setTrigger(false);
        setRowCount(0);
        return;
      }

      setRowCount((totalPages ?? 0) * PAGE_SIZE);

      const monthsSet = new Set<string>();
      for (const a of alunos) {
        for (const c of a.cobrancas || []) {
          const mk = monthKey(c.dataPagamento);
          if (mk) monthsSet.add(mk);
        }
      }
      const months = Array.from(monthsSet).sort();
      const newExtraColumns = months.map((m) => ({
        field: m,
        headerName: monthLabel(m),
        minWidth: 110,
        isNumber: true,
        valueFormatter: safeValueFormatter,
      }));

      newExtraColumns.push({
        field: "totalGeral",
        headerName: "Total",
        minWidth: 120,
        isNumber: true,
        valueFormatter: safeValueFormatter,
      });

      const newRows = alunos.map((a: any) => {
        const perMonth: Record<string, number> = {};
        let totalGeral = 0;

        for (const c of a.cobrancas || []) {
          const mk = monthKey(c.dataPagamento);
          if (!mk) continue;

          const vp = moneyToNumber(c.valorPago);
          if (!vp) continue;

          perMonth[mk] = (perMonth[mk] || 0) + vp;
          totalGeral += vp;
        }

        const monthFields: Record<string, number | null> = {};
        for (const m of months) {
          monthFields[m] = perMonth[m] ?? null; // null => em branco
        }

        return {
          id: a.id,
          nome: a.nome,
          ...monthFields,
          totalGeral,
        };
      });

      const totalRow: any = { id: "__TOTAL__", nome: "TOTAL" };

      for (const m of months) {
        const sumMonth = newRows.reduce(
          (acc: number, r: any) => acc + (r[m] || 0),
          0
        );
        totalRow[m] = sumMonth === 0 ? null : sumMonth; // se quiser sempre mostrar 0, troque por sumMonth
      }

      const totalTela = newRows.reduce(
        (acc: number, r: any) => acc + (r.totalGeral || 0),
        0
      );
      totalRow.totalGeral = totalTela === 0 ? null : totalTela;

      const rowsWithTotal = [...newRows, totalRow];

      const newVisibleCols = ["nome", ...months, "totalGeral"];

      setExtraColumns(newExtraColumns);
      setVisibleCols(newVisibleCols);
      setRows(rowsWithTotal);

      setTrigger(true);
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    const allRows = await fetchAllPages();

    if (allRows.length === 0) return;

    const doc = new jsPDF({ orientation: "landscape" });
    const cols = visibleCols;

    const head = [cols.map((c) => monthHeader(c))];

    const body = allRows.map((r: any) =>
      cols.map((c) => {
        const v = r?.[c];
        if (c === "nome") return v ?? "";
        return v == null ? "" : formatBRL(v);
      })
    );

    const finalTotalRow = cols.map((c) => {
      if (c === "nome") return "TOTAL GERAL";
      const sum = allRows.reduce(
        (acc, curr) => acc + (Number(curr[c]) || 0),
        0
      );
      return sum === 0 ? "" : formatBRL(sum);
    });
    body.push(finalTotalRow);

    doc.text("Financeiro - Relatório Completo", 14, 12);
    autoTable(doc, {
      head,
      body,
      startY: 18,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save("financeiro-completo.pdf");
  };

  const csvEscape = (v: any) => {
    const s = (v ?? "").toString();
    // envolve em aspas se tiver ; " ou quebra de linha
    if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const monthHeader = (c: string) => {
    if (c === "nome") return "Aluno";
    if (c === "totalGeral") return "Total";
    if (/^\d{4}-\d{2}$/.test(c)) return `${c.slice(5, 7)}/${c.slice(0, 4)}`; // MM/YYYY
    return c;
  };

  const exportCsv = async () => {
    const allRows = await fetchAllPages();

    if (allRows.length === 0) {
      alert("Não há dados para exportar.");
      return;
    }

    const cols = visibleCols;

    const headerLine = cols.map(monthHeader).map(csvEscape).join(";");

    const lines = allRows.map((r: any) => {
      return cols
        .map((c) => {
          const v = r?.[c];

          if (c === "nome") return csvEscape(v ?? "");

          const out = v == null ? "" : formatBRL(v);

          return csvEscape(out);
        })
        .join(";");
    });

    const totalLine = cols
      .map((c) => {
        if (c === "nome") return csvEscape("TOTAL GERAL");
        const sum = allRows.reduce(
          (acc, curr) => acc + (Number(curr[c]) || 0),
          0
        );
        return csvEscape(sum === 0 ? "" : formatBRL(sum));
      })
      .join(";");

    const csv = "\uFEFF" + [headerLine, ...lines, totalLine].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "financeiro-alunos-completo.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Grid wrap="wrap">
        <Grid
          size={{ xs: 6, sm: 7, md: 8, xl: 10 }}
          style={{
            marginTop: "-1.6rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 className={css(styles.pageTitle)} style={{ marginRight: "4px" }}>
            Consulta de alunos
          </h3>
          {trigger ? (
            <>
              <div style={{ display: "flex", gap: "15px" }}>
                <button className={css(styles.topBtn)} onClick={exportPdf}>
                  <PictureAsPdf />
                </button>
                <button className={css(styles.topBtn)} onClick={exportCsv}>
                  <TableView />
                </button>
              </div>
            </>
          ) : null}
        </Grid>
      </Grid>
      <hr
        style={{
          color: "#eee",
          marginTop: "-0.55rem",
          marginBottom: "1rem",
        }}
      />

      <FilterFinanceiro
        searchStudents={searchStudents}
        searchNome={searchNome}
        setSearchNome={setSearchNome}
        dataInicial={dataInicial}
        setDataInicial={setDataInicial}
        dataFinal={dataFinal}
        setDataFinal={setDataFinal}
        searchSituacao={searchSituacao}
        setSearchSituacao={setSearchSituacao}
      />

      {trigger ? (
        <>
          <Box sx={{ width: "100%", display: "grid", gap: 2, marginTop: 2 }}>
            <CustomDatagrid
              rows={rows}
              extraColumns={extraColumns}
              visibleColumns={visibleCols}
              // totals={totals}
              loading={loading}
              paginationMode="server"
              paginationModel={{ page, pageSize: PAGE_SIZE }}
              rowCount={rowCount}
              onPaginationModelChange={(model) => {
                if (model.page !== page) setPage(model.page);
              }}
            />
          </Box>
        </>
      ) : null}

      {loading ? (
        <Box
          width={"100%"}
          height={"45vh"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Grid
            size={{ xs: 12, md: 12 }}
            textAlign={"center"}
            justifyContent={"center"}
            alignItems={"center"}
            bgcolor={"#eceff9ff"}
            borderRadius={5}
            padding={5}
            color={Colors.noResultFont}
          >
            <div
              style={{
                marginTop: "-0.5rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress color="primary" />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <p
                style={{
                  color: "#4b4b4b",
                  fontSize: 14,
                  marginBottom: "-1rem",
                }}
              >
                {titleLoading}
              </p>
            </div>
          </Grid>
        </Box>
      ) : null}

      {notFound ? (
        <Box
          width={"100%"}
          height={"45vh"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Grid
            size={{ xs: 12, md: 12 }}
            height={{ md: "20%", xs: "50%" }}
            display={"inline-flex"}
            textAlign={"center"}
            justifyContent={"center"}
            alignItems={"center"}
            bgcolor={"lightyellow"}
            borderRadius={5}
            padding={5}
            color={Colors.noResultFont}
          >
            <p
              style={{
                color: "#4b4b4b",
                fontSize: 14,
              }}
            >
              Sua pesquisa não retornou nenhum resultado.
            </p>
          </Grid>
        </Box>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  pageTitle: {
    fontFamily: "Poppins",
    fontWeight: 600,
    color: "#4b4b4b",
  },
  input: {
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 400,
    color: "#808080",
    transitionDuration: "0.2s",
  },
  btn: {
    backgroundColor: "#669966",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#669966",
    borderRadius: 6,
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    padding: "0.5rem 0.3rem 0.42rem 0.4rem",
    width: "100%",
    marginTop: "1.95rem",
    transitionDuration: "0.2s",
    ":hover": {
      color: "#669966",
      backgroundColor: "transparent",
      borderColor: "#669966",
    },
    ":active": {
      color: "#669966",
      backgroundColor: "transparent",
      borderColor: "#669966",
      opacity: 0.6,
    },
  },
  btnDisabled: {
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderRadius: 6,
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    padding: "0.5rem 0.5rem 0.5rem 0.5rem",
    transitionDuration: "0.2s",
    backgroundColor: "#B3B3B3",
    borderColor: "#B3B3B3",
  },
  erro: {
    fontSize: 14,
    color: "red",
    textAlign: "center",
    fontWeight: 600,
    marginTop: "1rem",
    marginBottom: "-0.5rem",
  },
  topBtn: {
    cursor: "pointer",
    backgroundColor: "#004AAD",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#004AAD",
    borderRadius: 6,
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    padding: "0.5rem 0.5rem 0.5rem 0.5rem",
    transitionDuration: "0.2s",
    ":hover": {
      color: "#186ad6ff",
      backgroundColor: "transparent",
      borderColor: "#186ad6ff",
    },
    ":active": {
      color: "#004AAD",
      backgroundColor: "transparent",
      borderColor: "#004AAD",
      opacity: 0.6,
    },
  },
});
